import React, { useMemo, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { Edit, Power, PowerOff, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';

export interface ZehMotocaUsuarioRow {
  id: string;
  email: string;
  phone?: string;
  cpfcnpj?: string;
  clienteFornecedor?: {
    id: string;
    nome: string;
    codigo?: string;
    cpfCnpj?: string;
    email?: string;
    telefone?: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ZehMotocaUsuariosTableProps {
  data: ZehMotocaUsuarioRow[];
  onEdit: (usuario: ZehMotocaUsuarioRow) => void;
  onToggleStatus: (usuario: ZehMotocaUsuarioRow) => void;
  onResetPassword: (usuario: ZehMotocaUsuarioRow) => void;
}

const columnHelper = createColumnHelper<ZehMotocaUsuarioRow>();

function formatDatePtBr(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const ZehMotocaUsuariosTable: React.FC<ZehMotocaUsuariosTableProps> = ({
  data,
  onEdit,
  onToggleStatus,
  onResetPassword,
}) => {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'email', desc: false }]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('email', {
        header: 'E-mail',
        cell: (info) => <span className="font-medium">{info.getValue()}</span>,
      }),
      columnHelper.accessor((row) => row.clienteFornecedor?.nome ?? '—', {
        id: 'cliente',
        header: 'Cliente',
        cell: (info) => {
          const row = info.row.original;
          const codigo = row.clienteFornecedor?.codigo;
          return (
            <div>
              <div>{info.getValue()}</div>
              {codigo ? <div className="text-xs text-muted-foreground">Cód. {codigo}</div> : null}
            </div>
          );
        },
      }),
      columnHelper.accessor(
        (row) => row.cpfcnpj || row.clienteFornecedor?.cpfCnpj || '—',
        { id: 'cpfCnpj', header: 'CPF/CNPJ' }
      ),
      columnHelper.accessor(
        (row) => row.phone || row.clienteFornecedor?.telefone || '—',
        { id: 'telefone', header: 'Telefone' }
      ),
      columnHelper.accessor('isActive', {
        header: 'Status',
        cell: (info) => (
          <Badge variant={info.getValue() ? 'default' : 'secondary'}>
            {info.getValue() ? 'Ativo' : 'Inativo'}
          </Badge>
        ),
      }),
      columnHelper.accessor('createdAt', {
        header: 'Cadastro',
        cell: (info) => (
          <span className="whitespace-nowrap text-muted-foreground">{formatDatePtBr(info.getValue())}</span>
        ),
      }),
      columnHelper.display({
        id: 'acoes',
        header: 'Ações',
        enableSorting: false,
        cell: ({ row }) => {
          const usuario = row.original;
          return (
            <div className="flex items-center justify-end gap-1">
              <Button variant="outline" size="sm" onClick={() => onEdit(usuario)} title="Editar">
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onToggleStatus(usuario)}
                title={usuario.isActive ? 'Desativar' : 'Ativar'}
              >
                {usuario.isActive ? (
                  <PowerOff className="w-4 h-4 text-red-500" />
                ) : (
                  <Power className="w-4 h-4 text-green-500" />
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onResetPassword(usuario)}
                title="Resetar senha"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          );
        },
      }),
    ],
    [onEdit, onToggleStatus, onResetPassword]
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (data.length === 0) {
    return <p className="py-12 text-center text-muted-foreground">Nenhum usuário encontrado</p>;
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const canSort = header.column.getCanSort();
              const sorted = header.column.getIsSorted();
              return (
                <TableHead
                  key={header.id}
                  className={canSort ? 'cursor-pointer select-none' : undefined}
                  onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                >
                  {header.isPlaceholder ? null : (
                    <span className="inline-flex items-center gap-1">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {sorted === 'asc' && <span className="text-xs opacity-60">↑</span>}
                      {sorted === 'desc' && <span className="text-xs opacity-60">↓</span>}
                    </span>
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id} className={cell.column.id === 'acoes' ? 'text-right' : undefined}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
